"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Card from "components/Card"
import Button from "components/Button"
import { Plus, ChevronRight } from "lucide-react"
import InputField from "components/InputField"
import { toast } from "react-toastify"
import ErrorBox from "components/ErrorBox"

import { getDepartmentWithLevel, createDepartment, getLevels, createDepartmentLevel } from "api/apiHelper"

interface Level {
  id: string
  code: string
  order: number
  promotion_timeline: string
}

interface Department {
  id: string
  name: string
  levels: Level[]
  createdAt: string
}

const DepartmentManagement: React.FC = () => {
  const navigate = useNavigate()
  const [departments, setDepartments] = useState<Department[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [departmentName, setDepartmentName] = useState("")
  const [availableLevels, setAvailableLevels] = useState<Level[]>([])
  const [selectedLevels, setSelectedLevels] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDepartments()
    fetchAvailableLevels()
  }, [])

  const fetchAvailableLevels = async () => {
    try {
      const response = await getLevels()
      setAvailableLevels(response.data || [])
    } catch (err: any) {
      toast.error("Failed to fetch available levels")
      console.error("Error fetching levels:", err)
    }
  }

  const fetchDepartments = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getDepartmentWithLevel()

      // Transform API response to match our interface
      const transformedDepartments = response.data.map((dept: any) => ({
        id: dept.id,
        name: dept.name,
        levels: dept.levels || [],
        createdAt: dept.created_at || dept.createdAt || new Date().toISOString(),
      }))

      setDepartments(transformedDepartments)
    } catch (error) {
      console.error("Error fetching departments:", error)
      toast.error("Failed to fetch departments")
    } finally {
      setLoading(false)
    }
  }

  const handleAddDepartment = () => {
    setShowAddForm(true)
    setDepartmentName("")
    setSelectedLevels([])
    setError(null)
  }

  const handleLevelToggle = (levelId: string) => {
    setSelectedLevels((prev) => (prev.includes(levelId) ? prev.filter((id) => id !== levelId) : [...prev, levelId]))
  }

  const handleSelectAll = () => {
    setSelectedLevels(availableLevels.map((level) => level.id))
  }

  const handleDeselectAll = () => {
    setSelectedLevels([])
  }

  const handleCreateDepartment = async () => {
    if (!departmentName.trim()) {
      setError("Department name is required")
      return
    }

    if (selectedLevels.length === 0) {
      setError("Please select at least one level to create the department")
      return
    }
    setError(null)

    try {
      setLoading(true)
      setError(null)

      const departmentData = {
        name: departmentName.trim(),
      }

      console.log("Creating department:", departmentData)
      const departmentResponse = await createDepartment(departmentData)

      if (!departmentResponse?.data?.id) {
        throw new Error("Department creation failed - no ID returned")
      }

      const newDepartmentId = departmentResponse.data.id
      console.log("Department created with ID:", newDepartmentId)

      const selectedLevelObjects = availableLevels.filter((level) => selectedLevels.includes(level.id))
      const departmentLevelPromises = selectedLevelObjects.map(async (level) => {
        try {
          const departmentLevelData = {
            department: newDepartmentId,
            level: level.id,
          }
          console.log("Creating department-level association:", departmentLevelData)
          return await createDepartmentLevel(departmentLevelData)
        } catch (error) {
          console.error(`Failed to create department-level association for level ${level.code}:`, error)
          toast.error(`Failed to create department-level association for level ${level.code}`)
          throw error
        }
      })

      await Promise.all(departmentLevelPromises)
      console.log("All department-level associations created successfully")

      await fetchDepartments()

      toast.success("Department created successfully")
      setShowAddForm(false)
      setDepartmentName("")
      setSelectedLevels([])
    } catch (err: any) {
      console.error("Error creating department:", err)
      toast.error("Failed to create department")
      let errorMessage = "Failed to create department"

      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleDepartmentClick = (departmentId: string) => {
    navigate(`/organization-structure/levels/departments/${departmentId}`)
  }

  const cancelForm = () => {
    setShowAddForm(false)
    setDepartmentName("")
    setSelectedLevels([])
    setError(null)
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card>
        <ErrorBox errorMessage={error} />

        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Department Management</h1>
            <Button onClick={handleAddDepartment} className="flex items-center bg-brand-500 hover:bg-brand-600 dark:hover:bg-brand-700 dark:bg-brand-400 text-white text-sm gap-2" disabled={loading}>
              <Plus className="w-4 h-4" />
              Add Department
            </Button>
          </div>
          <p className="text-gray-600">Create and manage departments with organizational levels</p>
        </div>
      </Card>

      {showAddForm && (
        <Card extra="border-blue-200 bg-blue-50">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">Create New Department</h2>
            <p className="text-gray-600 mb-4">
              Select which organizational levels you want to include in this department
            </p>

            <div className="space-y-4">
              <InputField
                id="departmentName"
                label="Department Name"
                placeholder="Enter department name"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                extra=""
                variant=""
              />

              {availableLevels.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      Select Levels for Department ({selectedLevels.length}/{availableLevels.length} selected):
                    </h4>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-gray-400">|</span>
                      <button
                        type="button"
                        onClick={handleDeselectAll}
                        className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {availableLevels.map((level) => (
                      <label
                        key={level.id}
                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedLevels.includes(level.id)
                            ? "border-blue-300 bg-blue-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLevels.includes(level.id)}
                          onChange={() => handleLevelToggle(level.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">{level.code}</span>
                            <span className="text-xs text-gray-500">Order: {level.order}</span>
                          </div>
                          {level.promotion_timeline && (
                            <div className="text-xs text-gray-600 mt-1">Timeline: {level.promotion_timeline}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>

                  {selectedLevels.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <h5 className="text-xs font-medium text-blue-900 mb-2">Selected Levels:</h5>
                      <div className="flex flex-wrap gap-2">
                        {availableLevels
                          .filter((level) => selectedLevels.includes(level.id))
                          .map((level) => (
                            <span
                              key={level.id}
                              className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium"
                            >
                              {level.code}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  className="flex items-center bg-brand-500 hover:bg-brand-600 dark:hover:bg-brand-700 dark:bg-brand-400 text-white gap-2"
                  onClick={handleCreateDepartment}
                >
                  {loading ? "Creating..." : "Create Department"}
                </Button>
                <Button className="bg-red-500 hover:bg-red-600 dark:hover:bg-red-700 dark:bg-red-400  text-white" onClick={cancelForm}>
                  Cancel
                </Button>
              </div>

              {availableLevels.length > 0 && selectedLevels.length === 0 && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="text-orange-800 text-sm">
                    <strong>Note:</strong> Please select at least one level to create the department.
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white mb-2">Created Departments</h2>
          <p className="text-gray-600 mb-4">Click on any department to configure titles for each level</p>

          {loading && !showAddForm ? (
            <div className="py-8 text-center text-gray-500">Loading departments...</div>
          ) : departments.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              No departments created yet. Click "Add Department" to get started.
            </div>
          ) : (
            <div className="grid gap-4">
              {departments.map((department) => (
                <Card
                  key={department.id}
                  extra="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleDepartmentClick(department.id)}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg text-navy-700 dark:text-white">{department.name}</h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {department.levels.map((level) => (
                            <span
                              key={level.id}
                              className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium"
                            >
                              {level.code}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}

export default DepartmentManagement
